import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { FaFacebook, FaInstagram } from "react-icons/fa";
import "./MeetTheFarmer.css";

const MeetTheFarmer = ({ farm }) => {
  const { t } = useTranslation();
  const [lightboxImg, setLightboxImg] = useState(null);

  const hasName = farm.ownerFirstName || farm.ownerLastName;

  // A name alone says nothing a shopper does not already know from the farm title. Without at
  // least one filled-in detail this renders as an empty panel, so don't render it at all.
  const hasSubstance = Boolean(
    farm.farmerQuote ||
      farm.farmingStory ||
      farm.farmingPhilosophy ||
      farm.yearStartedFarming ||
      farm.farmingPractices ||
      farm.customPractices ||
      farm.certifications ||
      farm.customCertifications ||
      farm.photoGalleryUrls ||
      farm.socialFacebook ||
      farm.socialInstagram
  );

  if (!hasName || !hasSubstance) return null;

  const fullName = [farm.ownerFirstName, farm.ownerLastName].filter(Boolean).join(" ");
  const initials = [farm.ownerFirstName, farm.ownerLastName]
    .filter(Boolean)
    .map((n) => n.charAt(0).toUpperCase())
    .join("");

  const practiceKeys = farm.farmingPractices ? farm.farmingPractices.split(",").map((k) => k.trim()).filter(Boolean) : [];
  const certKeys = farm.certifications ? farm.certifications.split(",").map((k) => k.trim()).filter(Boolean) : [];
  const photos = farm.photoGalleryUrls ? farm.photoGalleryUrls.split(",").map((u) => u.trim()).filter(Boolean) : [];

  const hasSocial = farm.socialFacebook || farm.socialInstagram;

  return (
    <section className="meet-farmer">
      <h2 className="meet-farmer-title">{t("farmerProfile.meet_the_farmer")}</h2>

      <div className="meet-farmer-header">
        <div className="meet-farmer-avatar">
          {farm.ownerProfilePictureUrl ? (
            <img src={farm.ownerProfilePictureUrl} alt={fullName} />
          ) : (
            <div className="meet-farmer-initials">{initials}</div>
          )}
        </div>
        <div className="meet-farmer-info">
          <h3 className="meet-farmer-name">{fullName}</h3>
          {farm.farmerQuote && (
            <p className="meet-farmer-quote">"{farm.farmerQuote}"</p>
          )}
          {farm.yearStartedFarming && (
            <span className="meet-farmer-badge">
              {t("farmerProfile.farming_since", { year: farm.yearStartedFarming })}
            </span>
          )}
        </div>
      </div>

      {farm.farmingStory && (
        <div className="meet-farmer-section">
          <h4>{t("farmerProfile.our_story")}</h4>
          <p>{farm.farmingStory}</p>
        </div>
      )}

      {farm.farmingPhilosophy && (
        <div className="meet-farmer-section">
          <h4>{t("farmerProfile.philosophy")}</h4>
          <p>{farm.farmingPhilosophy}</p>
        </div>
      )}

      {(practiceKeys.length > 0 || farm.customPractices) && (
        <div className="meet-farmer-section">
          <h4>{t("farmerProfile.practices")}</h4>
          {practiceKeys.length > 0 && (
            <div className="meet-farmer-tags">
              {practiceKeys.map((key) => (
                <span key={key} className="meet-farmer-tag">
                  {t(`farmerProfile.tag_${key}`, { defaultValue: key })}
                </span>
              ))}
            </div>
          )}
          {farm.customPractices && (
            <p className="meet-farmer-custom">{farm.customPractices}</p>
          )}
        </div>
      )}

      {(certKeys.length > 0 || farm.customCertifications) && (
        <div className="meet-farmer-section">
          <h4>{t("farmerProfile.certifications")}</h4>
          {certKeys.length > 0 && (
            <div className="meet-farmer-tags">
              {certKeys.map((key) => (
                <span key={key} className="meet-farmer-tag cert">
                  {t(`farmerProfile.tag_${key}`, { defaultValue: key })}
                </span>
              ))}
            </div>
          )}
          {farm.customCertifications && (
            <p className="meet-farmer-custom">{farm.customCertifications}</p>
          )}
        </div>
      )}

      {photos.length > 0 && (
        <div className="meet-farmer-section">
          <h4>{t("farmerProfile.photo_gallery")}</h4>
          <div className="meet-farmer-gallery">
            {photos.map((url, i) => (
              <img
                key={i}
                src={url}
                alt={`${fullName} farm ${i + 1}`}
                className="meet-farmer-gallery-img"
                onClick={() => setLightboxImg(url)}
                loading="lazy"
              />
            ))}
          </div>
        </div>
      )}

      {hasSocial && (
        <div className="meet-farmer-social">
          {farm.socialFacebook && (
            <a href={farm.socialFacebook} target="_blank" rel="noopener noreferrer" className="meet-farmer-social-link">
              <FaFacebook /> Facebook
            </a>
          )}
          {farm.socialInstagram && (
            <a href={farm.socialInstagram} target="_blank" rel="noopener noreferrer" className="meet-farmer-social-link">
              <FaInstagram /> Instagram
            </a>
          )}
        </div>
      )}

      {lightboxImg && (
        <div className="meet-farmer-lightbox" onClick={() => setLightboxImg(null)}>
          <img src={lightboxImg} alt={`${fullName} farm view`} />
        </div>
      )}
    </section>
  );
};

export default MeetTheFarmer;
